import { useState } from 'react';
import { api } from '../../lib/api';
import type { Item } from '../../lib/types';
import { SearchSelect } from '../../components/SearchSelect';
import { Field, TextInput, NumberInput, TextArea, FormGrid } from '../../components/ui';
import { SourceSearch } from './SourceSearch';
import type { PieceSelection, SourceDelivery, SourceLine } from './SourceSearch';

// 详情页「添加工件」面板：
//  · 从历史来源添加：复用 SourceSearch（逐件勾选/按销售合同平铺/分页/按工件定位）→ POST /:id/items；
//    同一交货行多序列号时按件逐条提交（带 serial_number），勾 1 件只添加 1 件。
//  · 手工登记工件：物料 + 序列号/内部识别号 + 数量 + 问题 → POST /:id/items（asset_id 可空，来源确认时建内部身份）。
// 来源确认后新增的工件需单独 confirm-source，不会自动核验（后端守卫同口径）。

type PickRow = {
  piece: PieceSelection;
  delivery: SourceDelivery;
  line: SourceLine;
  inspectionId: string;
  quantity: number;
  problem: string;
};

const pieceText = (line: SourceLine, piece: PieceSelection) =>
  piece.serial_number || line.customer_serial || line.internal_code || line.serial_numbers || line.description_es || '工件';

export function AddItemPanel({ batchId, customerId, onChanged, onAdded, onClose }: {
  batchId: string;
  customerId?: string | null;
  onChanged: () => void;
  /** 添加成功后回传提示（父面板会因重载暂时卸载本组件，提示需由父级展示）。 */
  onAdded?: (message: string) => void;
  onClose: () => void;
}) {
  const base = `/api/rework-batches/${encodeURIComponent(batchId)}`;
  const [mode, setMode] = useState<'history' | 'manual'>('history');
  const [rows, setRows] = useState<PickRow[]>([]);
  const [manual, setManual] = useState({ itemId: '', itemLabel: '', identifier: '', quantity: '1', problem: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const togglePiece = (d: SourceDelivery, line: SourceLine, piece: PieceSelection, checked: boolean) => {
    setErr('');
    setMsg('');
    setRows((cur) => {
      const exists = cur.some((r) => r.piece.piece_key === piece.piece_key);
      if (!checked) return cur.filter((r) => r.piece.piece_key !== piece.piece_key);
      if (exists) return cur;
      const insp = piece.original_inspection;
      return [...cur, {
        piece,
        delivery: d,
        line,
        inspectionId: insp.match === 'unique' ? (insp.resolved_id || '') : '',
        quantity: 1,
        problem: '',
      }];
    });
  };

  const patchRow = (pieceKey: string, patch: Partial<PickRow>) => {
    setRows((cur) => cur.map((r) => (r.piece.piece_key === pieceKey ? { ...r, ...patch } : r)));
  };

  // 历史来源：逐件 POST /:id/items。后端 addItem 接受单件 body；失败逐条汇总，避免一件失败整批静默。
  const addFromHistory = async () => {
    if (!rows.length) { setErr('请先勾选要添加的历史工件'); return; }
    setBusy(true); setErr(''); setMsg('');
    let ok = 0;
    const fails: string[] = [];
    for (const r of rows) {
      try {
        await api.post(`${base}/items`, {
          asset_id: r.piece.asset_id || r.line.asset_id || undefined,
          item_id: r.line.item_id || undefined,
          // 逐件选择固定 1 件；旧响应整行选择保留可编辑数量。
          quantity: r.piece.legacy_line ? r.quantity : 1,
          original_delivery_id: r.delivery.id,
          original_delivery_line_id: r.line.id,
          // 传序列号 → 后端把该交货行收窄到这一件并单独定位原检验（同 line 多件互不覆盖）。
          serial_number: r.piece.legacy_line ? undefined : (r.piece.serial_number || undefined),
          original_inspection_id: r.inspectionId || undefined,
          problem: r.problem.trim() || undefined,
        });
        ok += 1;
      } catch (e: any) {
        fails.push(`${pieceText(r.line, r.piece)}：${e?.message || '添加失败'}`);
      }
    }
    setBusy(false);
    if (fails.length) {
      setErr(`已添加 ${ok} 件，${fails.length} 件失败：${fails.join('；')}`);
    } else {
      const done = `已添加 ${ok} 件工件。来源确认后新增的工件需单独确认来源，不会自动核验。`;
      setRows([]);
      setMsg(done);
      onAdded?.(done);
      onChanged();
    }
  };

  // 手工登记：无 asset_id，后端在 buildItemRow 先落草稿行，来源确认时再建内部工件身份。
  const addManual = async () => {
    if (!manual.itemId) { setErr('请选择生产物料'); return; }
    const qty = Number(manual.quantity);
    if (!(qty > 0)) { setErr('数量需大于 0'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.post(`${base}/items`, {
        item_id: manual.itemId,
        internal_identifier: manual.identifier.trim() || undefined,
        quantity: qty,
        problem: manual.problem.trim() || undefined,
      });
      setManual({ itemId: '', itemLabel: '', identifier: '', quantity: '1', problem: '' });
      const done = '工件已登记（草稿）。来源确认后新增的工件需单独确认来源，不会自动核验。';
      setMsg(done);
      onAdded?.(done);
      onChanged();
    } catch (e: any) {
      setErr(e?.message || '登记失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ margin: '8px 0', padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn ${mode === 'history' ? 'primary' : ''}`} onClick={() => setMode('history')}>从历史来源添加</button>
        <button className={`btn ${mode === 'manual' ? 'primary' : ''}`} onClick={() => setMode('manual')}>手工登记工件</button>
        <div className="spacer" />
        <button className="btn" onClick={onClose}>收起</button>
      </div>
      <div className="muted" style={{ fontSize: 12, margin: '6px 0' }}>
        来源确认后新增的工件需单独确认来源（confirm-source），不会自动核验；已确认来源的工件不可移除/换绑。
      </div>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      {mode === 'history' ? (
        <div>
          {!customerId ? <div className="notice">该批次缺少客户信息，无法检索历史来源；可切到「手工登记工件」。</div> : (
            <SourceSearch
              customerId={customerId}
              selectedKeys={rows.map((r) => r.piece.piece_key)}
              onTogglePiece={togglePiece}
              hint="逐件勾选历史工件加入本次添加；同一交货行多序列号可只选其中不合格的几件；已在返修中的工件会在来源确认时被拦截。"
            />
          )}
          {rows.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <div className="strong" style={{ marginBottom: 4 }}>本次添加（{rows.length} 件）</div>
              <table className="grid">
                <thead>
                  <tr><th>工件 / 序列号</th><th>来源交货</th><th style={{ width: 90 }}>数量</th><th>问题</th><th style={{ width: 220 }}>原检验</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const insp = r.piece.original_inspection;
                    return (
                      <tr key={r.piece.piece_key}>
                        <td className="muted">{pieceText(r.line, r.piece)}</td>
                        <td className="muted">{r.delivery.number}{r.delivery.contract_number ? ` / ${r.delivery.contract_number}` : ''}</td>
                        <td>
                          {r.piece.legacy_line ? (
                            <NumberInput
                              min={1}
                              value={String(r.quantity)}
                              onChange={(e) => patchRow(r.piece.piece_key, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                            />
                          ) : (
                            <span className="muted" title="逐件选择固定 1 件">1（单件）</span>
                          )}
                        </td>
                        <td>
                          <TextInput
                            value={r.problem}
                            placeholder="本件问题（可选）"
                            onChange={(e) => patchRow(r.piece.piece_key, { problem: e.target.value })}
                          />
                        </td>
                        <td>
                          {insp.match === 'unique' ? (
                            <span className="muted">自动关联 {insp.candidates?.[0]?.number || insp.resolved_id}</span>
                          ) : insp.match === 'multiple' || insp.match === 'weak' ? (
                            <select
                              className="sel"
                              value={r.inspectionId}
                              onChange={(e) => patchRow(r.piece.piece_key, { inspectionId: e.target.value })}
                              aria-label="选择原检验单"
                            >
                              <option value="">请选择原检验单…</option>
                              {(insp.candidates || []).map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.number}{c.result ? ` · ${c.result}` : ''}{c.inspection_date ? ` · ${c.inspection_date}` : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ color: 'var(--bad)' }}>未定位原检验；由质检确认模板并记录原因</span>
                          )}
                        </td>
                        <td><button className="btn" onClick={() => setRows((cur) => cur.filter((x) => x.piece.piece_key !== r.piece.piece_key))}>移除</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="detail-actions" style={{ marginTop: 6 }}>
                <button className="btn primary" disabled={busy} onClick={() => void addFromHistory()}>
                  {busy ? '添加中…' : `添加所选 ${rows.length} 件`}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <FormGrid>
          <Field label="生产物料 *" required>
            <SearchSelect<Item>
              endpoint="/api/items"
              value={manual.itemId || null}
              label={manual.itemLabel}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`}
              placeholder="检索生产物料…"
              onChange={(it) => setManual((s) => ({ ...s, itemId: it?.id || '', itemLabel: it ? `${it.code} ${it.name_zh}` : '' }))}
            />
          </Field>
          <Field label="序列号 / 内部识别号" hint="可留空，来源确认时由系统生成 INT- 内部编号">
            <TextInput value={manual.identifier} onChange={(e) => setManual((s) => ({ ...s, identifier: e.target.value }))} placeholder="客户序列号或内部识别号（可选）" />
          </Field>
          <Field label="数量 *" required>
            <NumberInput min={1} value={manual.quantity} onChange={(e) => setManual((s) => ({ ...s, quantity: e.target.value }))} />
          </Field>
          <Field label="问题描述" span>
            <TextArea value={manual.problem} onChange={(e) => setManual((s) => ({ ...s, problem: e.target.value }))} placeholder="该工件的问题/返工要求…" />
          </Field>
          <div className="detail-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn primary" disabled={busy} onClick={() => void addManual()}>{busy ? '登记中…' : '登记工件'}</button>
          </div>
        </FormGrid>
      )}
    </div>
  );
}
